import React, { useRef } from "react";
import { Doughnut } from "react-chartjs-2";
import {Chart as ChartJS,ArcElement,Tooltip,Legend,ChartOptions} from "chart.js";
import { numberSeparator } from "@/hooks/useThousandsSeparator";
import StackKit from "../../kit/stack";


ChartJS.register(ArcElement, Tooltip, Legend);

type DonutChartProps = {
  seriesData: number[];
  labelsData?: string[];
  options?: ChartOptions<"doughnut">;
};

const DonutChart: React.FC<DonutChartProps> = ({seriesData,labelsData = [],options = {}}) => {
  
  const chartRef = useRef<any>(null);
  const labels = labelsData.length ? labelsData : ["اعتبار هدیه","فروش نقدی خارجی","فروش IPG ( انلاین)","اعتبار خارجی","اعتبار داخلی","فروش نقدی داخلی",];
  const defaultOptions: ChartOptions<"doughnut"> = {
    cutout: "80%",
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (context) => {
            const label = context.label || "";
            const value = context.raw || 0;
            const total = (context.chart.data.datasets[0].data || []).reduce(
              (sum: number, val) => sum + (typeof val === "number" ? val : 0),
              0
            );
            const formattedValue = numberSeparator(+value);
            const percentage = ((+value / total) * 100).toFixed(1);
            return `${label}: ${formattedValue} (${percentage}%)`;
          },
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  const gradientPlugin = {
    id: "gradientPlugin",
    afterLayout: (chart: any) => {
      const ctx = chart.ctx;
      const { top, bottom, left } = chart.chartArea;

      const gradients = [
        ["#9F0000", "#390000"],
        ["#A1F5FF", "#000"],
        ["#F9C4B5", "#3E1105"],
        ["#7BC8B5", "#3C6259"],
        ["#7B9D95", "#030303ff"],
        ["#EB6440", "#853924"],
      ].map(([start, end]) => {
        const gradient = ctx.createLinearGradient(left, top, left, bottom);
        gradient.addColorStop(0, start);
        gradient.addColorStop(1, end);
        return gradient;
      });
      chart.data.datasets[0].backgroundColor = gradients;
    },
  };

  const centerTextPlugin = {
    id: "centerText",
    afterDraw: (chart: any) => {
      const { width } = chart;
      const { top, bottom, left, right } = chart.chartArea;
      const ctx = chart.ctx;

      const dataset = chart.data.datasets[0].data || [];
      const total = dataset.reduce((sum: number, val: number) => sum + (typeof val === "number" ? val : 0),0);
      const totalText = numberSeparator(total);
      const currencyText = `${totalText} ریال`;

      ctx.save();
      ctx.font = `bold ${Math.max(14, width / 25)}px sans-serif`; 
      ctx.fillStyle = "#fff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(currencyText, (left + right) / 2, (top + bottom) / 2);
      ctx.restore();
    },
  };

  const data = {
    labels,
    datasets: [
      {
        data: seriesData,
        backgroundColor: [],
        borderWidth: 0,
      },
    ],
  };

  return (
    <StackKit
      justifyContent="center"
      alignItems="center"
      padding={0}
      sx={{width: "100%",maxWidth: "300px",margin: "0 auto",aspectRatio: "1 / 1",position: "relative",
        "@media (max-width: 600px)": {maxWidth: "250px",},
        "@media (max-width: 400px)": {maxWidth: "200px",},
      }}
    >
      <Doughnut ref={chartRef} data={data} options={{ ...defaultOptions, ...options }} plugins={[gradientPlugin, centerTextPlugin]}/>
    </StackKit>
  );
};

export default DonutChart;
